import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import Footer from "../Footer/Footer";
import GLOBALS from "../../utils/Globals";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { getUserProfileAction } from "../../redux/slices/users/usersSlice";
import { Alert, Spinner, Button, Form, Row, Col } from "react-bootstrap";
import { FaTriangleExclamation, FaWifi } from "react-icons/fa6";
import { FaSearch } from "react-icons/fa";
import { MdRefresh } from "react-icons/md";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import { CellClickedEvent } from "ag-grid-community";
import "../AgGrid/styles.css";
import SlidingPane from "react-sliding-pane";
import "react-sliding-pane/dist/react-sliding-pane.css";
import "./style.css";
import logo from "../../assets/img/verified-barcode.svg"
import {
  LicenseManager,
  RowApiModule,
  HighlightChangesModule,
  ValidationModule,
} from "ag-grid-enterprise";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-community/styles/ag-theme-material.css";
import ButtonRendererSearch from "../AgGrid/ButtonRendererSearch";

function SearchDataHub() {
  const [state, setState] = useState({
    isPaneOpen: false,
    isPaneOpenLeft: false,
  });

  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Search form state
  const [searchParams, setSearchParams] = useState({
    companyName: '',
    brandName: '',
    productDescription: '',
    labelDescription: '',
    gtin: '',
    gtinStatus: '',
    industry: '',
    targetMarket: '',
    globalProductClassification: '',
    packagingLevel: '',
    pageSize: 100,
    page: 1
  });

  const gridRef = useRef();

  const baseURLSearchDataHub =
    process.env.REACT_APP_ENV === "PROD"
      ? GLOBALS.PROD.baseURLSearchDataHub
      : GLOBALS.DEV.baseURLSearchDataHub;

  const gridOptions = {
    autoSizeStrategy: {
      type: "fitCellContents",
    },
  };

  const [colDefs, setColDefs] = useState([
    {
      field: "CompanyName",
      filter: "agTextColumnFilter",
      headerName: "Company Name",
      width: 200,
    },
    {
      field: "BrandName",
      filter: "agTextColumnFilter",
      headerName: "Brand Name",
      width: 150,
    },
    {
      field: "ProductDescription",
      filter: "agTextColumnFilter",
      headerName: "Product Description",
      width: 300,
    },
    {
      field: "LabelDescription",
      filter: "agTextColumnFilter",
      headerName: "Label Description",
      width: 200,
    },
    {
      field: "PackagingLevel",
      filter: "agTextColumnFilter",
      headerName: "Packaging Level",
      width: 150,
    },
    {
      field: "Industry",
      filter: "agTextColumnFilter",
      headerName: "Industry",
      width: 120,
    },
    {
      field: "Status",
      headerName: "Status",
      cellClassRules: {
        "red-cell": (p) => p.value === "Inactive",
        "green-cell": (p) => p.value === "In Use",
      },
      filter: "agTextColumnFilter",
      width: 100,
    },
  ]);

  const defaultColDef = useMemo(() => {
    return {
      editable: false,
      enableRowGroup: true,
      enablePivot: true,
      enableValue: true,
      sortable: true,
      resizable: true,
      filter: true,
      flex: 1,
      minWidth: 100,
    };
  }, []);

  const rowSelection = useMemo(() => {
    return {
      mode: "singleRow",
      checkboxes: false,
      enableClickSelection: true,
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if at least one search parameter is provided
    const hasSearchParams = Object.entries(searchParams).some(([key, value]) => 
      key !== 'pageSize' && key !== 'page' && value && value.trim() !== ''
    );

    if (!hasSearchParams) {
      setSearchError({
        type: 'validation',
        message: 'Please provide at least one search parameter.'
      });
      return;
    }

    try {
      setSearchLoading(true);
      setSearchError(null);
      setSearchResults(null);

      // Filter out empty parameters
      const filteredParams = Object.entries(searchParams).reduce((acc, [key, value]) => {
        if (value && value.toString().trim() !== '') {
          acc[key] = value.toString().trim();
        }
        return acc;
      }, {});

      console.log('Sending search request with params:', filteredParams);

      const response = await axios.post(baseURLSearchDataHub, filteredParams);

      console.log('Search response:', response.data);
      setSearchResults(response.data);

    } catch (err) {
      console.error("Error searching Data Hub:", err);

      let errorMessage = 'Failed to search Data Hub. Please try again.';
      let errorType = 'general';

      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = 'Unauthorized access. Please check your GS1 API key configuration.';
          errorType = 'auth';
        } else if (err.response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
          errorType = 'server';
        } else if (err.response.data && err.response.data.error) {
          errorMessage = err.response.data.error;
          errorType = 'api';
        }
      } else if (err.request) {
        errorMessage = 'Network error. Please check your connection.';
        errorType = 'network';
      }

      setSearchError({ type: errorType, message: errorMessage });
    } finally {
      setSearchLoading(false);
    }
  };

  const onCellClicked = useCallback((event) => {
    console.log('Cell clicked:', event);
    // Open detail panel with the clicked row data
    setSelectedProduct(event.data);
    setState(prev => ({ ...prev, isPaneOpen: true }));
  }, []);

  const clearSearch = () => {
    setSearchParams({
      companyName: '',
      brandName: '',
      productDescription: '',
      labelDescription: '',
      gtin: '',
      gtinStatus: '',
      industry: '',
      targetMarket: '',
      globalProductClassification: '',
      packagingLevel: '',
      pageSize: 100,
      page: 1
    });
    setSearchResults(null);
    setSearchError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center items-center mb-4">
            <img src={logo} alt="GS1 Data Hub" className="w-16 h-16 mr-4" />
            <h1 className="text-4xl font-bold text-gray-800">
              Search Data Hub
            </h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Search the GS1 US Data Hub for comprehensive product information using various search criteria.
          </p>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white shadow-md rounded-xl p-6 space-y-4 mb-6"
        >
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Company Name</Form.Label>
                <Form.Control
                  type="text"
                  name="companyName"
                  value={searchParams.companyName}
                  onChange={handleInputChange}
                  placeholder="Enter company name"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Brand Name</Form.Label>
                <Form.Control
                  type="text"
                  name="brandName"
                  value={searchParams.brandName}
                  onChange={handleInputChange}
                  placeholder="Enter brand name"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Product Description</Form.Label>
                <Form.Control
                  type="text"
                  name="productDescription"
                  value={searchParams.productDescription}
                  onChange={handleInputChange}
                  placeholder="Enter product description"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>GTIN</Form.Label>
                <Form.Control
                  type="text"
                  name="gtin"
                  value={searchParams.gtin}
                  onChange={handleInputChange}
                  placeholder="Enter GTIN"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>GTIN Status</Form.Label>
                <Form.Select
                  name="gtinStatus"
                  value={searchParams.gtinStatus}
                  onChange={handleInputChange}
                >
                  <option value="">Select Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Discontinued">Discontinued</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Industry</Form.Label>
                <Form.Select
                  name="industry"
                  value={searchParams.industry}
                  onChange={handleInputChange}
                >
                  <option value="">Select Industry</option>
                  <option value="General">General</option>
                  <option value="CPG">CPG</option>
                  <option value="Regulated Healthcare">Regulated Healthcare</option>
                  <option value="Foodservice">Foodservice</option>
                  <option value="Apparel">Apparel</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Packaging Level</Form.Label>
                <Form.Select
                  name="packagingLevel"
                  value={searchParams.packagingLevel}
                  onChange={handleInputChange}
                >
                  <option value="">Select Packaging Level</option>
                  <option value="Each">Each</option>
                  <option value="Case as Each">Case as Each</option>
                  <option value="Inner Pack">Inner Pack</option>
                  <option value="Case">Case</option>
                  <option value="Display Shipper">Display Shipper</option>
                  <option value="Mixed Case">Mixed Case</option>
                  <option value="Pallet">Pallet</option>
                  <option value="Mixed Pallet">Mixed Pallet</option>
                  <option value="Unit of Use">Unit of Use</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex gap-3 justify-content-center">
            <Button
              type="submit"
              variant="outline-primary"
              disabled={searchLoading}
              className="d-flex align-items-center gap-2"
            >
              {searchLoading ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Searching...
                </>
              ) : (
                <>
                  <FaSearch />
                  Search Data Hub
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline-secondary"
              onClick={clearSearch}
              disabled={searchLoading}
            >
              Clear
            </Button>
          </div>
        </motion.form>

        {/* Error Display */}
        {searchError && (
          <Alert variant="danger" className="mb-4">
            <div className="d-flex align-items-center">
              <FaTriangleExclamation className="me-2" />
              <div>
                <strong>Search Error</strong>
                <p className="mb-0">{searchError.message}</p>
              </div>
            </div>
          </Alert>
        )}

        {/* Results Display */}
        {searchResults && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white shadow-md rounded-xl p-6"
          >
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h3>Search Results</h3>
              <div className="text-muted">
                {searchResults.products ? `${searchResults.products.length} results found` : 'No results'}
              </div>
            </div>

            {searchResults.products && searchResults.products.length > 0 ? (
              <div
                className="ag-theme-material"
                style={{ height: 600 }}
              >
                <AgGridReact
                  ref={gridRef}
                  rowData={searchResults.products}
                  gridOptions={gridOptions}
                  animateRows={true}
                  columnDefs={colDefs}
                  rowSelection={rowSelection}
                  pagination={true}
                  paginationPageSize={20}
                  paginationPageSizeSelector={[10, 20, 50]}
                  defaultColDef={defaultColDef}
                  onCellClicked={onCellClicked}
                />
              </div>
            ) : (
              <Alert variant="info" className="text-center">
                <h5>No Results Found</h5>
                <p className="mb-0">
                  No products were found matching your search criteria. Please try different search parameters.
                </p>
              </Alert>
            )}
          </motion.div>
        )}
      </div>

      {/* Product Detail Panel */}
      <SlidingPane
        isOpen={state.isPaneOpen}
        title={selectedProduct ? `Product Details: ${selectedProduct.ProductDescription || 'N/A'}` : 'Product Details'}
        subtitle={selectedProduct ? `${selectedProduct.CompanyName || 'N/A'} - ${selectedProduct.BrandName || 'N/A'}` : ''}
        onRequestClose={() => {
          setState(prev => ({ ...prev, isPaneOpen: false }));
          setSelectedProduct(null);
        }}
        width="600px"
        from="right"
      >
        {selectedProduct && (
          <div className="product-detail-panel">
            <div className="detail-section">
              <h5 className="detail-section-title">Basic Information</h5>
              <div className="detail-grid">
                <div className="detail-item">
                  <strong>Company Name:</strong>
                  <span>{selectedProduct.CompanyName || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <strong>Brand Name:</strong>
                  <span>{selectedProduct.BrandName || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <strong>Product Description:</strong>
                  <span>{selectedProduct.ProductDescription || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <strong>Label Description:</strong>
                  <span>{selectedProduct.LabelDescription || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <strong>Status:</strong>
                  <span className={`status-badge ${selectedProduct.Status === 'In Use' ? 'status-active' : 'status-inactive'}`}>
                    {selectedProduct.Status || 'N/A'}
                  </span>
                </div>
                <div className="detail-item">
                  <strong>Industry:</strong>
                  <span>{selectedProduct.Industry || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <strong>Packaging Level:</strong>
                  <span>{selectedProduct.PackagingLevel || 'N/A'}</span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h5 className="detail-section-title">Additional Details</h5>
              <div className="detail-grid">
                <div className="detail-item">
                  <strong>Target Market:</strong>
                  <span>{selectedProduct.TargetMarket || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <strong>Brand Name 2:</strong>
                  <span>{selectedProduct.BrandName2 || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <strong>Brand Language 1:</strong>
                  <span>{selectedProduct.BrandNameLanguage1 || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <strong>Brand Language 2:</strong>
                  <span>{selectedProduct.BrandNameLanguage2 || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <strong>Company URI:</strong>
                  <span>{selectedProduct.CompanyURI || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Raw Data Section */}
            <div className="detail-section">
              <h5 className="detail-section-title">Raw Data</h5>
              <div className="raw-data-container">
                <pre className="raw-data">
                  {JSON.stringify(selectedProduct, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        )}
      </SlidingPane>

      <Footer />
    </div>
  );
}

export default SearchDataHub;
